import { useState, useRef, useCallback, useEffect } from "react";
import LoadingSpinner from "@/components/ui/LoadingSpinner.jsx";
import ErrorMessage from "@/components/ui/ErrorMessage.jsx";
import SearchableDropdown from "@/components/molecules/SearchableDropdown.jsx";
import { awsService } from "@/services/api/awsService";
import { accountsService } from "@/services/api/api";
import { Search, User, ShieldX } from "lucide-react";

function PermissionRemoveForm() {
  // Organization state
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState(null);

  // User search state
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const searchTimeoutRef = useRef(null);
  const dropdownRef = useRef(null);

  // User assignments (permissions across accounts)
  const [assignments, setAssignments] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [selectedAssignments, setSelectedAssignments] = useState([]);

  // SR Ticket
  const [srTicket, setSrTicket] = useState("");

  // Submit state
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Load organizations on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setDataLoading(true);
        setDataError(null);
        const orgsRes = await accountsService.getOrganizations();
        setOrganizations(orgsRes.data || []);
      } catch (error) {
        console.error("Error loading data:", error);
        setDataError("Error loading form data. Please try again.");
      } finally {
        setDataLoading(false);
      }
    };
    loadData();
  }, []);

  const handleOrganizationSelect = (org) => {
    setSelectedOrganization(org);
    // Reset user and assignments when org changes
    setSearchText("");
    setSelectedUser(null);
    setAssignments([]);
    setSelectedAssignments([]);
  };

  // User search
  const handleSearchChange = useCallback((value) => {
    setSearchText(value);
    if (selectedUser) {
      setSelectedUser(null);
      setAssignments([]);
      setSelectedAssignments([]);
    }
    setShowDropdown(true);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (value.trim().length < 3) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const data = await awsService.searchADUsers(value.trim());
        const results = Array.isArray(data) ? data : data?.results || data?.users || [];
        setSearchResults(results);
        setShowDropdown(true);
      } catch (error) {
        console.error("Error searching users:", error);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 400);
  }, [selectedUser]);

  const handleSelectUser = async (user) => {
    const username = user.UserPrincipalName || user.Email || user.SamAccountName || user.Name || "";
    setSearchText(username);
    setSelectedUser(user);
    setShowDropdown(false);
    setSearchResults([]);

    // Load user assignments (permissions across accounts)
    if (selectedOrganization) {
      setAssignmentsLoading(true);
      setAssignments([]);
      setSelectedAssignments([]);
      try {
        const assignedName = user.UserPrincipalName || user.SamAccountName || "";
        const response = await accountsService.getPermissionSetsByUser(assignedName);
        setAssignments(response.data || []);
      } catch (error) {
        console.error("Error loading user assignments:", error);
        setAssignments([]);
      } finally {
        setAssignmentsLoading(false);
      }
    }
  };

  // Toggle assignment selection
  const toggleAssignment = (index) => {
    setSelectedAssignments((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index);
      }
      return [...prev, index];
    });
  };

  // Select/Deselect all
  const toggleSelectAll = () => {
    if (selectedAssignments.length === assignments.length) {
      setSelectedAssignments([]);
    } else {
      setSelectedAssignments(assignments.map((_, idx) => idx));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedOrganization) {
      setMessage({ type: "error", text: "Please select an Organization." });
      return;
    }
    // TODO: Temporary restriction - only LLA organization allowed
    if (selectedOrganization.CloudOrganizationUid?.toLowerCase() !== 'lla') {
      setMessage({ type: "error", text: "Permission removal is currently only available for the LLA organization." });
      return;
    }
    if (!selectedUser) {
      setMessage({ type: "error", text: "Please select a User." });
      return;
    }
    if (selectedAssignments.length === 0) {
      setMessage({ type: "error", text: "Please select at least one permission to remove." });
      return;
    }

    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const assignmentsToRemove = selectedAssignments.map((idx) => {
        const assignment = assignments[idx];
        return {
          "account-id": assignment.AccountId || "",
          "account-name": assignment.AccountName || "",
          "sso-instance-arn": assignment.SsoInstanceArn || "",
          "permission-set-arn": assignment.PermissionSetArn || "",
          "identity-store-id": assignment.SsoInstanceIdentityStoreId || "",
          "assigned-to-type": "USER",
          "assigned-to-id": assignment.AssignedToId || "",
          "assigned-to-name": assignment.AssignedToName || searchText,
          "Info": srTicket || ""
        };
      });

      const payload = {
        AwsOrganization: selectedOrganization.CloudOrganizationUid || "",
        AssignmentsToAdd: [],
        AssignmentsToRemove: assignmentsToRemove,
        sr: srTicket || "",
      };

      console.log("Payload to submit:", payload);

      await awsService.updatePermissionSetAssignments(payload);

      setMessage({
        type: "success",
        text: `${selectedAssignments.length} permission(s) removed successfully from user "${searchText}".`,
      });

      // Reset form
      setSearchText("");
      setSelectedUser(null);
      setAssignments([]);
      setSelectedAssignments([]);
      setSrTicket("");
    } catch (error) {
      setMessage({
        type: "error",
        text: error.message || "An error occurred while removing permissions.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (dataLoading) {
    return <LoadingSpinner message="Loading form data..." />;
  }

  if (dataError) {
    return <ErrorMessage error={dataError} showRetry={true} />;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {message.text && (
        <div
          className={`p-4 rounded-lg border ${
            message.type === "success"
              ? "bg-green-100 border-green-400 text-green-700"
              : "bg-red-100 border-red-400 text-red-700"
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Section 1: Organization */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-6 border-b border-gray-200 pb-3">
          Organization
        </h3>
        <SearchableDropdown
          key="org-remove"
          items={organizations}
          searchKey="CloudOrganizationUid"
          displayKey="CloudOrganizationName"
          placeholder="Search Organization"
          label="Organization"
          onSelect={handleOrganizationSelect}
          required
        />
        {selectedOrganization && selectedOrganization.CloudOrganizationUid?.toLowerCase() !== 'lla' && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-700 font-medium">
              ⚠️ Permission removal is currently only available for the LLA organization.
            </p>
          </div>
        )}
      </div>

      {/* Section 2: User Search */}
      {selectedOrganization && (
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 border-b border-gray-200 pb-3">
            User
          </h3>
          <div className="relative" ref={dropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search User <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={searchText}
                onChange={(e) => handleSearchChange(e.target.value)}
                onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
                onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                  selectedUser ? "border-green-300 bg-green-50" : "border-gray-300"
                }`}
                placeholder="Search user (min 3 characters)..."
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                {searchLoading ? (
                  <div className="w-4 h-4 border-2 border-gray-200 border-t-orange-500 rounded-full animate-spin"></div>
                ) : (
                  <Search size={16} className="text-gray-400" />
                )}
              </div>
            </div>

            {/* Selected user info */}
            {selectedUser && (
              <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-xs text-gray-600">
                  {selectedUser.EmailAddress || selectedUser.UserPrincipalName || selectedUser.Email || ""}
                </p>
              </div>
            )}

            {/* Dropdown results */}
            {showDropdown && searchResults.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {searchResults.map((user, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleSelectUser(user)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-orange-50 transition-colors border-b border-gray-50 last:border-b-0"
                  >
                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <User size={14} className="text-gray-500" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {user.Name || user.DisplayName || user.SamAccountName || "Unknown"}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {user.EmailAddress || user.UserPrincipalName || user.Email || ""}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* No results */}
            {showDropdown && searchText.length >= 3 && !searchLoading && searchResults.length === 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center">
                <p className="text-sm text-gray-500">No users found for &quot;{searchText}&quot;</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Section 3: User Assignments (permissions to remove) */}
      {selectedUser && (
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-6 border-b border-gray-200 pb-3">
            <h3 className="text-lg font-semibold text-gray-900">
              Current Permissions
            </h3>
            {assignments.length > 0 && (
              <button
                type="button"
                onClick={toggleSelectAll}
                className="text-sm text-orange-600 hover:text-orange-700 font-medium"
              >
                {selectedAssignments.length === assignments.length ? "Deselect All" : "Select All"}
              </button>
            )}
          </div>

          {assignmentsLoading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner message="Loading user permissions..." />
            </div>
          ) : assignments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <ShieldX size={40} className="mx-auto mb-3 text-gray-300" />
              <p className="text-sm">No permissions found for this user in the selected organization.</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {assignments.map((assignment, idx) => (
                <label
                  key={idx}
                  className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-colors ${
                    selectedAssignments.includes(idx)
                      ? "border-red-300 bg-red-50"
                      : "border-gray-100 bg-gray-50 hover:bg-gray-100"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedAssignments.includes(idx)}
                    onChange={() => toggleAssignment(idx)}
                    className="w-4 h-4 text-red-500 border-gray-300 rounded focus:ring-red-500"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-gray-900">
                        {assignment.AccountName || assignment.AccountId || "Unknown Account"} ( {assignment.AccountId || "N/A"} )
                      </span>
                      <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded">
                        {assignment.PermissionSetName || "Unknown Permission Set"}
                      </span>
                    </div>
                    
                  </div>
                </label>
              ))}
            </div>
          )}

          {selectedAssignments.length > 0 && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700 font-medium">
                {selectedAssignments.length} permission(s) selected for removal
              </p>
            </div>
          )}
        </div>
      )}

      {/* Section 4: SR Ticket */}
      {selectedUser && (
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 border-b border-gray-200 pb-3">
            Additional Information
          </h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              SR Ticket
            </label>
            <input
              type="text"
              value={srTicket}
              onChange={(e) => setSrTicket(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="e.g. SR-12345"
            />
          </div>
        </div>
      )}

      {/* Submit */}
      {selectedUser && (
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <button
            type="submit"
            disabled={loading || selectedAssignments.length === 0 || (selectedOrganization && selectedOrganization.CloudOrganizationUid?.toLowerCase() !== 'lla')}
            className="w-full bg-red-500 text-white px-6 py-3 rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          >
            {loading ? (
              <span className="flex items-center justify-center space-x-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                <span>Removing...</span>
              </span>
            ) : (
              `Remove ${selectedAssignments.length > 0 ? selectedAssignments.length : ""} Permission${selectedAssignments.length !== 1 ? "s" : ""}`
            )}
          </button>
        </div>
      )}
    </form>
  );
}

export default PermissionRemoveForm;
